import { FC, useContext, useEffect, useMemo, useState } from "react";
import { InfoContext, UIContext } from "../../../shared/contexts";
import { Form } from "antd";
import VirtualOfficeService from "../../../shared/services/virtualOffice";
import { FormsListTable } from "./components/FormList";
import { PageHeader } from "../../../shared/components/MainLayout/components";
import { SUBFOLDERS_TYPES } from "../shared/enums/documents-subfolders";
import { InputFactory, RowSize } from "../../../shared/components";
import { getInputsFilter } from "../shared/utils/inputsFormDocuments";
import "./index.css";
import MasterService from "../../../shared/services/master";

interface FormsDocumentsProps {
  title: string;
  subFolder: SUBFOLDERS_TYPES;
}

export const FormsDocuments: FC<FormsDocumentsProps> = ({ title, subFolder }) => {
  const { setShowError } = useContext(UIContext);
  const { setTitle } = useContext(InfoContext);

  const [allDocuments, setAllDocuments] = useState<any[]>([]);
  const [documentsList, setDocumentsList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [branchesCombo, setBranchesCombo] = useState<any[]>([]);

  const [form] = Form.useForm();

  useEffect(() => {
    setTitle(title);
    getComboBranch();
    fetchDocuments();
  }, []);

  const getComboBranch = async () => {
    try {
      const response = await MasterService.getBranchesForCombo();
      if (response.status === 200) {
        setBranchesCombo(response.data);
      }
    } catch {
    }
  };

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await VirtualOfficeService.getFormsList(subFolder);
      const documentsWithMetadata = await Promise.all(
        response.map(async (document) => {
          const metadata = await VirtualOfficeService.getMetadata(document.key);
          return {
            ...document,
            metadata,
          };
        })
      );

      setAllDocuments(documentsWithMetadata);
      setDocumentsList(documentsWithMetadata);
    } catch {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getBranchLabelsByValues = (values: any[]) => {
    return branchesCombo
      .filter((b: any) =>
        values.some((v) => String(v) === String(b.value))
      )
      .map((b: any) => b.label);
  };
  

  const normalize = (value: any) =>
    String(value ?? "")
      .trim()
      .toLowerCase();
  
  const applyFilters = () => {
    const filters = form.getFieldsValue(true);
  
    let filtered = [...allDocuments];
  
    const selectedBranchValues = Array.isArray(filters.branch)
      ? filters.branch
      : filters.branch !== undefined
      ? [filters.branch]
      : [];
  
    if (selectedBranchValues.length > 0) {
      const selectedBranchLabels = getBranchLabelsByValues(
        selectedBranchValues
      ).map(normalize);
  
      filtered = filtered.filter((doc) => {
        const metaBranch = normalize(doc.metadata?.branch);
        return selectedBranchLabels.includes(metaBranch);
      });
    }
  
    if (filters.keyword) {
      const keyword = normalize(filters.keyword);
      filtered = filtered.filter((doc) =>
        normalize(doc.metadata?.description).includes(keyword)
      );
    }
  
    setDocumentsList(filtered);
  };
  
  const cleanFilter = () => {
    form.resetFields();
    setDocumentsList(allDocuments);
  };

  const initialStateFilters = useMemo(
    () => ({
      branch: undefined,
      keyword: undefined,
    }),
    []
  );

  return (
    <>
      <PageHeader actions={undefined} />

      <RowSize rowProps={{ gutter: [0, 0] }} className="mb-2">
        <InputFactory
          form={form}
          items={getInputsFilter(branchesCombo)}
          onFilter={applyFilters}
          isLoading={false}
          initialState={initialStateFilters}
          onClear={cleanFilter}
        />
      </RowSize>

      <FormsListTable
        documentsList={documentsList}
        isLoading={isLoading}
      />
    </>
  );
};

export default FormsDocuments;
